import * as React from 'react';

import { useTranslations } from '@packages/i18n';
import { useRouter } from 'next/router';
import { ClickoutSegment } from '@packages/common';
import { useProductSearchById } from '@packages/services';
import { useDyClient } from '@lib/dy';
import { DyContext } from '@lib/dy/DyProvider';

import { RecommendationChannel } from '../RecommendationsContainer';
import { useDyRecommendations } from '../RecommendationsContainer/DyRecommendations/useDyRecommendations';
import { WishlistHelperContext, ClickoutProvider, CategoryContext } from '../../context';
import { deDuplicateProducts, sortAsProvided } from '../../containers/RecommendationsContainer/utils';

import { QuickList } from '../../components/QuickList';

export const QUICKLIST_PRODUCT_KEY = 'quicklist';

export const QuickListContainerBase: React.FC<{ productId: string }> = ({ productId }) => {
  const { client } = useDyClient();
  const { category } = React.useContext(CategoryContext);
  const { recommendations, title, decisionId } = useDyRecommendations({
    categoryTitle: category?.title,
    recommendationChannel: RecommendationChannel.MARKETING_BOX,
  });
  // Initial product must be shown as 1st one
  const [, , initialProduct] = useProductSearchById([productId], 'productId');
  const { wishlistHelper } = React.useContext(WishlistHelperContext);

  // TODO: Refactor to deduplucate
  const orderedProductIds = recommendations.map((slot) => slot.sku);
  const [, , productsResponse] = useProductSearchById(orderedProductIds, 'productId');

  const [products, setProducts] = React.useState(
    sortAsProvided(deDuplicateProducts(productsResponse), orderedProductIds),
  );

  React.useEffect(() => {
    const providedProducts = sortAsProvided(deDuplicateProducts(productsResponse), orderedProductIds);
    if (products.map((p) => p.productId).join(',') !== providedProducts.map((p) => p.productId).join(',')) {
      setProducts(providedProducts);
    }
  }, [orderedProductIds, products, productsResponse]);

  const handleDYRecoClick = (productId: string) => {
    const slotId = recommendations.find((slot) => slot.sku === productId)?.slotId;
    if (slotId) {
      client.engagements({
        engagements: [
          {
            type: 'SLOT_CLICK',
            decisionId,
            slotId,
          },
        ],
      });
    }
  };

  const headerTextFallback = useTranslations('quickListHeaderTextFallback') as string;

  // if initial product is missing, do not show quicklist
  if ((initialProduct?.hits || []).length === 0) {
    return null;
  }

  return (
    <QuickList
      headerText={(recommendations && title) || headerTextFallback}
      products={products}
      wishlistHelper={wishlistHelper}
      recommendationChannel={RecommendationChannel.MARKETING_BOX}
      categoryId={category && category._id}
      onRecoTileClick={handleDYRecoClick}
    />
  );
};

export const QuickListContainer: React.FC = () => {
  const { query } = useRouter();
  const dyContext = React.useContext(DyContext);

  const quicklistProductId = (query[QUICKLIST_PRODUCT_KEY] as string) || null;
  const updatedDyContext = React.useMemo(
    () => ({
      ...dyContext,
      context: {
        ...(dyContext?.context || {}),
        page: { ...(dyContext?.context?.page || {}), type: 'PRODUCT' as const, data: [quicklistProductId] },
      },
    }),
    [dyContext, quicklistProductId],
  );

  if (!quicklistProductId) {
    return null;
  }

  return (
    <DyContext.Provider value={updatedDyContext}>
      <ClickoutProvider
        segment={ClickoutSegment.RECOMMENDATION}
        recommendationChannel={RecommendationChannel.MARKETING_BOX}
        service={'product'}
      >
        <QuickListContainerBase productId={quicklistProductId}></QuickListContainerBase>
      </ClickoutProvider>
    </DyContext.Provider>
  );
};
